import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Car as CarIcon, CalendarDays, Search, AlertTriangle, CheckCircle2, Layers, Plus, Send, X, Trash2, Construction, PackageOpen } from "lucide-react";
import html2canvas from "html2canvas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  DAY_NAMES, MONTH_NAMES,
  COLORS, PROJECT_INACTIVE_CATEGORIES, DISMANTLE_WINDOW_DAYS,
  toISO, mondayOf, addDays, fmtRoDay, dowFromIso, isSaturdayIso, fmtRangeLabel,
} from "./utils";

/* eslint-disable no-unused-vars */

export default function DismantleDialog({ target, persons, cars, onClose, onSubmit, busy, projectId, projectColor }) {
  const [personIds, setPersonIds] = useState([]);
  const [carIds, setCarIds] = useState([]);
  const [note, setNote] = useState("");
  const [personSearch, setPersonSearch] = useState("");
  const [carSearch, setCarSearch] = useState("");
  // Inventory state for site leftovers picker
  const [invItems, setInvItems] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invSearch, setInvSearch] = useState("");
  const [invColorFilter, setInvColorFilter] = useState(projectColor || "all");
  const [invTypeFilter, setInvTypeFilter] = useState("all");
  // For each item: state in {undefined (ignore), "brought_back", "left_on_site"}
  const [invState, setInvState] = useState({});
  // Per item return_location override
  const [invReturnLoc, setInvReturnLoc] = useState({});
  const [invDefect, setInvDefect] = useState({});
  const [invFilterMode, setInvFilterMode] = useState("on_site"); // 'on_site' | 'team_color' | 'all'

  const todayIso = (() => {
    const d = new Date();
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  })();
  // Sugestie inițială: ziua de DUPĂ finalizarea șantierului (dacă există).
  // Altfel: ziua selectată în planificator. Altfel: azi.
  const defaultDismantleDate = (() => {
    if (target?.finalization_date) {
      try {
        const fd = new Date(String(target.finalization_date).slice(0, 10) + "T00:00:00");
        if (!isNaN(fd.getTime())) {
          fd.setDate(fd.getDate() + 1);
          const y = fd.getFullYear(), m = String(fd.getMonth() + 1).padStart(2, "0"), dd = String(fd.getDate()).padStart(2, "0");
          return `${y}-${m}-${dd}`;
        }
      } catch { /* fallthrough */ }
    }
    return target?.day || todayIso;
  })();
  const [dismantleDate, setDismantleDate] = useState(defaultDismantleDate);

  // Persoanele care au lucrat efectiv pe acest proiect (pontaje / alocări planificator)
  const [workerIds, setWorkerIds] = useState(null);
  const [showAllPersons, setShowAllPersons] = useState(false);
  useEffect(() => {
    if (!projectId) { setWorkerIds([]); return; }
    api.get(`/projects/${projectId}/workers`)
      .then((r) => setWorkerIds(r.data?.person_ids || []))
      .catch(() => setWorkerIds([]));
  }, [projectId]);

  // Load inventory once
  useEffect(() => {
    if (!projectId) return;
    setInvLoading(true);
    api.get("/inventory")
      .then((r) => {
        const all = (r.data || []).filter((it) => it.status !== "defect" && (it.quantity ?? 1) > 0);
        setInvItems(all);
        // Pre-select items already marked as on_site for this project as "brought_back" default
        const preState = {};
        const preLoc = {};
        all.forEach((it) => {
          const os = it.on_site || {};
          if (os.project_id === projectId && (os.status === "on_site" || os.status === "picked_up")) {
            preState[it.id] = "brought_back";
            preLoc[it.id] = os.original_location || it.location || "MAGAZIE";
          }
        });
        setInvState(preState);
        setInvReturnLoc(preLoc);
      })
      .catch(() => setInvItems([]))
      .finally(() => setInvLoading(false));
  }, [projectId]);

  const colorsAvail = useMemo(() => {
    const s = new Set();
    invItems.forEach((it) => { if (it.color) s.add(it.color); });
    return Array.from(s).sort();
  }, [invItems]);

  const invFiltered = useMemo(() => {
    const q = invSearch.trim().toLowerCase();
    let base = invItems;
    if (invFilterMode === "on_site") {
      base = invItems.filter((it) => (it.on_site || {}).project_id === projectId);
    } else if (invFilterMode === "team_color") {
      base = invItems.filter((it) => it.color && it.color === (projectColor || invColorFilter));
    }
    return base
      .filter((it) => invFilterMode !== "all" || invColorFilter === "all" || it.color === invColorFilter)
      .filter((it) => invTypeFilter === "all" || it.item_type === invTypeFilter)
      .filter((it) => !q || `${it.name || ""} ${it.serial || ""} ${it.assignee || ""}`.toLowerCase().includes(q))
      // Exclude items pe alt șantier
      .filter((it) => !(it.on_site && it.on_site.status === "on_site" && it.on_site.project_id && it.on_site.project_id !== projectId));
  }, [invItems, invSearch, invColorFilter, invTypeFilter, invFilterMode, projectId, projectColor]);

  const setItemAction = (id, action) => {
    setInvState((s) => {
      const n = { ...s };
      if (n[id] === action) delete n[id]; else n[id] = action;
      return n;
    });
  };

  const setReturnLoc = (id, val) => setInvReturnLoc((s) => ({ ...s, [id]: val }));
  const setDefect = (id, val) => setInvDefect((s) => ({ ...s, [id]: val }));

  const counts = useMemo(() => {
    let bb = 0, los = 0;
    Object.values(invState).forEach((v) => { if (v === "brought_back") bb += 1; if (v === "left_on_site") los += 1; });
    return { bb, los };
  }, [invState]);

  const allPersons = useMemo(() => {
    const list = (persons || []).filter((p) => !p.is_admin_user);
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro"));
  }, [persons]);

  // Subset: doar cei care au lucrat pe proiect (dacă există date); altfel toți
  const projectWorkers = useMemo(() => {
    if (!workerIds || workerIds.length === 0) return null;
    const s = new Set(workerIds);
    const subset = allPersons.filter((p) => s.has(p.id));
    return subset.length > 0 ? subset : null;
  }, [allPersons, workerIds]);

  const basePersons = (!showAllPersons && projectWorkers) ? projectWorkers : allPersons;

  const allCars = useMemo(() => {
    // Sortare: marca → model → culoare → numar_inmatriculare
    return [...(cars || [])].sort((a, b) => {
      const ma = (a.marca || "").toLowerCase();
      const mb = (b.marca || "").toLowerCase();
      if (ma !== mb) return ma.localeCompare(mb, "ro");
      const moa = (a.model || "").toLowerCase();
      const mob = (b.model || "").toLowerCase();
      if (moa !== mob) return moa.localeCompare(mob, "ro");
      const ca = (a.culoare || "").toLowerCase();
      const cb = (b.culoare || "").toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb, "ro");
      return (a.numar_inmatriculare || "").localeCompare(b.numar_inmatriculare || "", "ro");
    });
  }, [cars]);

  const filteredPersons = useMemo(() => {
    const q = personSearch.trim().toLowerCase();
    if (!q) return basePersons;
    return basePersons.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [basePersons, personSearch]);

  const filteredCars = useMemo(() => {
    const q = carSearch.trim().toLowerCase();
    if (!q) return allCars;
    return allCars.filter((c) => {
      const blob = `${c.label || ""} ${c.marca || ""} ${c.model || ""} ${c.culoare || ""} ${c.numar_inmatriculare || ""} ${c.intern_number || ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [allCars, carSearch]);

  const togglePerson = (id) => setPersonIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleCar = (id) => setCarIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleSubmit = () => {
    // Pregătește payload site-leftovers din invState
    const brought_back = [];
    const left_on_site = [];
    Object.entries(invState).forEach(([iid, action]) => {
      if (action === "brought_back") {
        brought_back.push({
          item_id: iid,
          return_location: (invReturnLoc[iid] || "").trim() || null,
          defect: !!invDefect[iid],
        });
      } else if (action === "left_on_site") {
        left_on_site.push(iid);
      }
    });
    onSubmit({ personIds, carIds, note, dismantleDate, brought_back, left_on_site });
  };

  const handleQuickSubmit = () => {
    if (!window.confirm("Sigur demontezi totul fără echipamente lăsate pe șantier?")) return;
    onSubmit({ personIds, carIds, note, dismantleDate, brought_back: [], left_on_site: [] });
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-700">
            🔧 Marcare demontaj
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500 mb-3">
          Proiect: <strong className="text-zinc-800">{target.label}</strong>. După confirmare, proiectul trece în categoria 7 — Demontat de Facturat și DISPARE din Planificator.
          <br/>
          <span className="text-emerald-700 font-semibold">ℹ Toate selecțiile (persoane, mașini, echipamente) sunt opționale</span> — poți confirma și fără să bifezi nimic.
        </div>
        <div className="mb-3 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="dismantle-quick-banner">
          <div className="text-xs text-emerald-900">
            <div className="font-bold text-sm">⚡ Demontaj rapid — nimic de lăsat pe șantier?</div>
            <div className="text-emerald-800">Confirmă într-un singur click, fără să mai parcurgi lista de echipamente.</div>
          </div>
          <Button
            onClick={handleQuickSubmit}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            data-testid="dismantle-quick-confirm"
          >
            ⚡ Demontat — totul OK
          </Button>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="small-label">👷 Cine a demontat? <span className="text-zinc-400 font-normal">(selectează una sau mai multe persoane)</span></Label>
            {projectWorkers && (
              <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px]" data-testid="dismantle-workers-filter-info">
                {!showAllPersons ? (
                  <>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                      ✓ Doar cei care au lucrat pe acest șantier ({projectWorkers.length})
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowAllPersons(true)}
                      className="text-[var(--primary)] hover:underline font-semibold"
                      data-testid="dismantle-show-all-persons"
                    >
                      Arată tot personalul ({allPersons.length})
                    </button>
                  </>
                ) : (
                  <>
                    <span className="px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-semibold">
                      Tot personalul ({allPersons.length})
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowAllPersons(false)}
                      className="text-emerald-700 hover:underline font-semibold"
                      data-testid="dismantle-show-workers-only"
                    >
                      ✓ Doar cei care au lucrat aici ({projectWorkers.length})
                    </button>
                  </>
                )}
              </div>
            )}
            <Input
              value={personSearch}
              onChange={(e) => setPersonSearch(e.target.value)}
              placeholder="🔍 Caută persoană (nume / prenume)..."
              className="mt-1.5 h-8 text-xs"
              data-testid="dismantle-person-search"
            />
            <div className="mt-1.5 grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-[200px] overflow-y-auto p-2 rounded border bg-zinc-50">
              {filteredPersons.map((p) => (
                <label key={p.id} className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${personIds.includes(p.id) ? "bg-emerald-100 border border-emerald-300" : "bg-white hover:bg-zinc-100 border border-zinc-200"}`}>
                  <input
                    type="checkbox"
                    checked={personIds.includes(p.id)}
                    onChange={() => togglePerson(p.id)}
                    data-testid={`dismantle-person-${p.id}`}
                  />
                  <span className="font-medium truncate">{p.name}</span>
                </label>
              ))}
              {filteredPersons.length === 0 && <div className="text-xs text-zinc-400 italic col-span-full text-center py-2">{personSearch ? "Nicio potrivire pentru căutare." : "Nicio persoană disponibilă."}</div>}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">{personIds.length} selectat(e){personSearch && ` · afișate ${filteredPersons.length}/${basePersons.length}`}</div>
          </div>
          <div>
            <Label className="small-label">🚗 Cu ce mașină? <span className="text-zinc-400 font-normal">(opțional, una sau mai multe)</span></Label>
            <Input
              value={carSearch}
              onChange={(e) => setCarSearch(e.target.value)}
              placeholder="🔍 Caută mașină (marcă / model / culoare / nr înmatriculare)..."
              className="mt-1.5 h-8 text-xs"
              data-testid="dismantle-car-search"
            />
            <div className="mt-1.5 grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-[200px] overflow-y-auto p-2 rounded border bg-zinc-50">
              {filteredCars.map((c) => {
                const subline = [c.marca, c.model, c.culoare].filter(Boolean).join(" · ");
                return (
                  <label key={c.id} className={`flex items-start gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${carIds.includes(c.id) ? "bg-blue-100 border border-blue-300" : "bg-white hover:bg-zinc-100 border border-zinc-200"}`}>
                    <input
                      type="checkbox"
                      checked={carIds.includes(c.id)}
                      onChange={() => toggleCar(c.id)}
                      data-testid={`dismantle-car-${c.id}`}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-bold truncate">{subline || "—"}</div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {c.numar_inmatriculare || "—"}{c.intern_number ? ` · #${c.intern_number}` : ""}
                      </div>
                    </div>
                  </label>
                );
              })}
              {filteredCars.length === 0 && <div className="text-xs text-zinc-400 italic col-span-full text-center py-2">{carSearch ? "Nicio potrivire pentru căutare." : "Nicio mașină disponibilă."}</div>}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">{carIds.length} selectată(e){carSearch && ` · afișate ${filteredCars.length}/${allCars.length}`}</div>
          </div>

          {/* ===== Site leftovers picker ===== */}
          {projectId && (
            <div className="border-2 border-orange-200 rounded-lg p-3 bg-orange-50/40" data-testid="dismantle-leftovers-section">
              <Label className="small-label flex items-center gap-1.5 text-orange-800">
                <Construction size={14} />
                📦 Echipamente șantier <span className="text-zinc-500 font-normal">— marchează ce a fost adus înapoi vs rămas</span>
              </Label>
              <div className="flex items-center gap-2 flex-wrap mt-2">
                <Select value={invFilterMode} onValueChange={setInvFilterMode}>
                  <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="dismantle-leftovers-mode"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_site">Doar marcate ca pe șantier</SelectItem>
                    {projectColor && <SelectItem value="team_color">Culoarea echipei ({projectColor})</SelectItem>}
                    <SelectItem value="all">Tot inventarul</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={invSearch}
                  onChange={(e) => setInvSearch(e.target.value)}
                  placeholder="🔍 caută..."
                  className="h-8 max-w-[180px] text-xs"
                  data-testid="dismantle-leftovers-search"
                />
                {invFilterMode === "all" && (
                  <Select value={invColorFilter} onValueChange={setInvColorFilter}>
                    <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate culorile</SelectItem>
                      {colorsAvail.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Select value={invTypeFilter} onValueChange={setInvTypeFilter}>
                  <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toate tipurile</SelectItem>
                    <SelectItem value="echipament">Echipament</SelectItem>
                    <SelectItem value="psa">PSA</SelectItem>
                    <SelectItem value="consumabil">Consumabil</SelectItem>
                  </SelectContent>
                </Select>
                <div className="ml-auto text-[11px] flex gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">↩ Adus: {counts.bb}</span>
                  <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 font-bold">🚧 Rămâne: {counts.los}</span>
                </div>
              </div>
              <div className="mt-2 max-h-[280px] overflow-y-auto space-y-1 pr-1" data-testid="dismantle-leftovers-list">
                {invLoading ? (
                  <div className="py-4 text-center text-xs text-zinc-500">Se încarcă inventar...</div>
                ) : invFiltered.length === 0 ? (
                  <div className="py-6 text-center text-xs text-zinc-500 italic">
                    {invFilterMode === "on_site" ? "Nicio piesă marcată ca lăsată pe șantier pentru acest proiect." : "Nicio piesă în filtru."}
                  </div>
                ) : (
                  invFiltered.map((it) => {
                    const action = invState[it.id];
                    const os = it.on_site || {};
                    const alreadyMarked = os.project_id === projectId;
                    return (
                      <div key={it.id} className={`rounded border p-2 text-xs flex items-center gap-2 flex-wrap ${action === "brought_back" ? "border-emerald-300 bg-emerald-50" : action === "left_on_site" ? "border-orange-300 bg-orange-50" : "border-zinc-200 bg-white"}`} data-testid={`dismantle-inv-row-${it.id}`}>
                        <div className="flex-1 min-w-[180px]">
                          <div className="font-medium flex items-center gap-1.5 flex-wrap">
                            <span>{it.name}</span>
                            {it.color && <span className="text-[9px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-700 px-1 py-0.5 rounded">{it.color}</span>}
                            {alreadyMarked && <span className="text-[9px] font-bold uppercase bg-orange-200 text-orange-900 px-1 py-0.5 rounded">PE ȘANTIER</span>}
                          </div>
                          <div className="text-[10px] text-zinc-500 mt-0.5">
                            {it.serial && <span className="font-mono mr-2">SN: {it.serial}</span>}
                            {it.location && <span>📍 {it.location}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant={action === "brought_back" ? "default" : "outline"}
                            onClick={() => setItemAction(it.id, "brought_back")}
                            className={`h-7 text-[11px] ${action === "brought_back" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"}`}
                            data-testid={`dismantle-inv-bb-${it.id}`}
                          >
                            ↩ Adus
                          </Button>
                          <Button
                            size="sm"
                            variant={action === "left_on_site" ? "default" : "outline"}
                            onClick={() => setItemAction(it.id, "left_on_site")}
                            className={`h-7 text-[11px] ${action === "left_on_site" ? "bg-orange-600 hover:bg-orange-700 text-white" : "border-orange-400 text-orange-700 hover:bg-orange-50"}`}
                            data-testid={`dismantle-inv-los-${it.id}`}
                          >
                            🚧 Rămâne
                          </Button>
                        </div>
                        {action === "brought_back" && (
                          <div className="basis-full flex items-center gap-2 mt-1 pl-2 border-l-2 border-emerald-300">
                            <span className="text-[10px] text-zinc-600">📍 Descarcă la:</span>
                            <Input
                              value={invReturnLoc[it.id] || (os.original_location || it.location || "MAGAZIE")}
                              onChange={(e) => setReturnLoc(it.id, e.target.value)}
                              className="h-7 text-xs flex-1 max-w-[200px]"
                              data-testid={`dismantle-inv-loc-${it.id}`}
                            />
                            <label className="flex items-center gap-1 text-[10px] text-red-700 font-semibold cursor-pointer">
                              <input type="checkbox" checked={!!invDefect[it.id]} onChange={(e) => setDefect(it.id, e.target.checked)} data-testid={`dismantle-inv-defect-${it.id}`}/>
                              ⚠ DEFECT
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <div>
            <Label className="small-label">📅 Data demontaj</Label>
            <Input
              type="date"
              value={dismantleDate}
              onChange={(e) => setDismantleDate(e.target.value)}
              className="mt-1"
              data-testid="dismantle-date"
            />
            <div className="text-[10px] text-zinc-500 mt-1">
              Default: {target?.finalization_date ? "ziua de după finalizarea șantierului" : (target?.day ? "ziua selectată în planificator" : "azi")}. Schimbă dacă demontajul s-a făcut în altă zi.
            </div>
          </div>
          <div>
            <Label className="small-label">📦 Materiale duse în magazie / Notă (opțional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ex: 12 panouri tip A · 3 căști · 1 set butelii · sau orice altă observație"
              className="mt-1 min-h-[60px]"
              data-testid="dismantle-note"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button
            onClick={handleSubmit}
            disabled={busy}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="dismantle-confirm"
          >
            🔧 {busy ? "Se procesează…" : "Confirmă demontaj"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
